

import { listenToOnlineWorkers, listenToAds, MAX_WORKER_RADIUS_KM } from '../services/firestoreService';
import { JobRequest, ServiceCategory, WorkerProfile, AdBanner as AdBannerType } from '../types';
import { WorkerCard } from './WorkerCard';
import { Button } from './ui/Button';
import { ChevronDown, ChevronUp, MapPin, Plus, Search, Layers, LocateFixed, Clock, Tag, IndianRupee, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../translations';
import { NotificationBell } from './NotificationBell';
import { UserRole } from '../types';
import { AdBanner } from './ui/AdBanner';

import { useSession } from '../context/SessionContext';

// Declare Google Maps types
declare var google: any;

interface CustomerViewProps {
    onSelectWorker: (worker: WorkerProfile) => void;
    onBookWorker: (worker: WorkerProfile) => void;
    onOpenPostJob: () => void;
    activeJobs?: JobRequest[];
    onTrackWorker?: (job: JobRequest) => void;
    categories: ServiceCategory[];
}

const MAP_STYLES = [
    { "featureType": "all", "elementType": "labels.text.fill", "stylers": [{ "color": "#1e293b" }] },
    { "featureType": "landscape", "elementType": "geometry.fill", "stylers": [{ "color": "#f8fafc" }] },
    { "featureType": "poi", "stylers": [{ "visibility": "off" }] },
    { "featureType": "road", "elementType": "geometry.fill", "stylers": [{ "color": "#ffffff" }] },
    { "featureType": "road", "elementType": "geometry.stroke", "stylers": [{ "color": "#e2e8f0" }] },
    { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#cbd5e1" }] }
];

// Helper: Create a robust circular marker icon using Canvas
const createMarkerIcon = (imageUrl: string, fallbackText: string = '') => {
    return new Promise<string>((resolve) => {
        const size = 80; // High res for crispness
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        if (!ctx) return resolve(imageUrl);

        const drawBaseCircle = () => {
            // Shadow
            ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
            ctx.shadowBlur = 10;
            ctx.shadowOffsetY = 4;

            // White Border Circle
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, (size / 2) - 4, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();

            // Reset Shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetY = 0;
        };

        const drawFallback = () => {
            drawBaseCircle();
            // Inner colored circle
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, (size / 2) - 8, 0, Math.PI * 2);
            ctx.fillStyle = '#3b82f6'; // Blue-500
            ctx.fill();

            // Text
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 30px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(fallbackText.charAt(0).toUpperCase(), size / 2, size / 2 + 2);

            resolve(canvas.toDataURL());
        };

        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.src = imageUrl;

        img.onload = () => {
            try {
                drawBaseCircle();

                // Clip Inner Area
                ctx.save();
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, (size / 2) - 8, 0, Math.PI * 2);
                ctx.clip();

                // Center Crop
                const aspect = img.width / img.height;
                let sw, sh, sx, sy;
                if (aspect > 1) {
                    sh = img.height;
                    sw = img.height;
                    sx = (img.width - img.height) / 2;
                    sy = 0;
                } else {
                    sw = img.width;
                    sh = img.width;
                    sx = 0;
                    sy = (img.height - img.width) / 2;
                }
                ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);
                ctx.restore();

                resolve(canvas.toDataURL());
            } catch (e) {
                drawFallback();
            }
        };

        img.onerror = () => drawFallback();
    });
};

export const CustomerView: React.FC<CustomerViewProps> = ({
    onSelectWorker,
    onBookWorker,
    onOpenPostJob,
    activeJobs = [],
    onTrackWorker,
    categories
}) => {
    // Location Auto-Detect
    const [userLocation, setUserLocation] = useState<{ lat: number, lng: number } | null>(null);
    const [locationName, setLocationName] = useState<string>('');
    const { feeConfig } = useSession();

    useEffect(() => {
        const cached = localStorage.getItem('hh_user_location');
        if (cached) {
            try { setUserLocation(JSON.parse(cached)); } catch (e) { }
        }
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(async (pos) => {
                const newLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setUserLocation(newLoc);
                localStorage.setItem('hh_user_location', JSON.stringify(newLoc));
                if (feeConfig.googleMapsApiKey) {
                    try {
                        const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${newLoc.lat},${newLoc.lng}&key=${feeConfig.googleMapsApiKey}`);
                        const data = await res.json();
                        if (data.results?.[0]) setLocationName(data.results[0].formatted_address);
                    } catch (e) { }
                }
            }, (err) => console.log("Location denied", err));
        }
    }, [feeConfig.googleMapsApiKey]);
    const [onlineWorkers, setOnlineWorkers] = useState<WorkerProfile[]>([]);
    const [query, setQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
    const [isSheetExpanded, setIsSheetExpanded] = useState(false);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
    const [ads, setAds] = useState<AdBannerType[]>([]);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const searchRef = useRef<HTMLDivElement>(null);

    // Handle clicking outside search to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsSearchFocused(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const lang = (localStorage.getItem('sl_lang') || 'en') as any;
    const t = useTranslation(lang);

    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstance = useRef<any>(null);
    const markersRef = useRef<{ [key: string]: any }>({});

    // Default Center (New Delhi - Connaught Place area)
    const defaultCenter = { lat: 28.6290, lng: 77.2180 };

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // Re-subscribe with new coords when userLocation changes
        const unsub = listenToOnlineWorkers(
            (workers) => { setOnlineWorkers(workers); },
            userLocation?.lat,
            userLocation?.lng,
            feeConfig.maxWorkerRadiusKm || 10
        );
        return () => unsub();
    }, [userLocation, feeConfig.maxWorkerRadiusKm]); // ✅ Re-run when GPS location or radius config is updated

    useEffect(() => {
        const unsub = listenToAds((fetchedAds) => {
            setAds(fetchedAds);
        });
        return () => unsub();
    }, []);

    // Initialize Map
    useEffect(() => {
        if (mapRef.current && !mapInstance.current && typeof google !== 'undefined') {
            mapInstance.current = new google.maps.Map(mapRef.current, {
                center: defaultCenter,
                zoom: 13,
                disableDefaultUI: true,
                styles: MAP_STYLES,
                zoomControl: false,
                gestureHandling: 'greedy',
                clickableIcons: false,
                mapId: "DEMO_MAP_ID"
            });
        }
    }, [userLocation]); // Re-run if userLocation changes (logic handles init vs update)

    // Pan to user location when found
    useEffect(() => {
        if (mapInstance.current && userLocation) {
            mapInstance.current.panTo(userLocation);
            mapInstance.current.setZoom(15);
            // Add User Marker
            // Add User Marker
            new google.maps.marker.AdvancedMarkerElement({
                map: mapInstance.current,
                position: userLocation,
                title: "You are here",
                content: ((() => {
                    const div = document.createElement('div');
                    div.style.width = '16px';
                    div.style.height = '16px';
                    div.style.backgroundColor = '#4285F4';
                    div.style.borderRadius = '50%';
                    div.style.border = '2px solid white';
                    div.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
                    return div;
                })())
            });
        }
    }, [userLocation]);

    const filteredWorkers = onlineWorkers.filter(w => {
        const matchesCat = selectedCategory === 'All' || w.skill === selectedCategory;
        const matchesQuery = !query ||
            w.name.toLowerCase().includes(query.toLowerCase()) ||
            w.skill.toLowerCase().includes(query.toLowerCase());
        // ✅ Configurable radius filter
        const radius = feeConfig.maxWorkerRadiusKm || 10;
        const withinRange = !userLocation || (w.distanceKm ?? 0) <= radius;
        return matchesCat && matchesQuery && withinRange;
    });

    // Get searchable items: Main Categories and Subcategories
    const searchableCategories = categories.filter(c => c.isActive).map(c => {
        const parent = c.parentId ? categories.find(p => p.id === c.parentId) : null;
        return {
            ...c,
            displayName: parent ? `${parent.name} › ${c.name}` : c.name,
            searchLabel: `${c.name} ${parent ? parent.name : ''}`.toLowerCase()
        };
    });

    const categoriesToDisplay = query
        ? searchableCategories.filter(c => c.searchLabel.includes(query.toLowerCase()))
        : searchableCategories;

    // Update Markers (Diffing Logic)
    useEffect(() => {
        if (!mapInstance.current || typeof google === 'undefined') return;

        const currentMarkers = markersRef.current;
        const activeWorkerIds = new Set();

        filteredWorkers.forEach(async (worker) => {
            activeWorkerIds.add(worker.id);

            // ✅ Use REAL GPS coords from the worker's profile (current_lat/current_lng)
            // Fall back to fake offsets only if real coords are unavailable
            let position: { lat: number; lng: number };
            if (worker.currentLat && worker.currentLng) {
                position = { lat: worker.currentLat, lng: worker.currentLng };
            } else {
                // Fallback: random nearby offset for demo purposes
                const latOffset = (50 - worker.coordinates.y) * 0.002;
                const lngOffset = (worker.coordinates.x - 50) * 0.002;
                const mapCenter = userLocation || defaultCenter;
                position = {
                    lat: mapCenter.lat + latOffset,
                    lng: mapCenter.lng + lngOffset
                };
            }

            if (currentMarkers[worker.id]) {
                // Update existing marker
                currentMarkers[worker.id].position = position;
                currentMarkers[worker.id].zIndex = selectedWorkerId === worker.id ? 100 : 1;
            } else {
                // Create new marker with custom content
                const iconUrl = await createMarkerIcon(worker.avatarUrl, worker.name);

                const buildContent = () => {
                    const img = document.createElement('img');
                    img.src = iconUrl;
                    img.style.width = '50px';
                    img.style.height = '50px';
                    // We can't easily replicate 'anchor' natively on the img without a wrapper, 
                    // but AdvancedMarker centers content by default if no pin.
                    // Actually, let's wrap it to match the previous anchor point logic if needed.
                    // But standard centering is usually fine for these bubbles.
                    return img;
                };

                const marker = new google.maps.marker.AdvancedMarkerElement({
                    map: mapInstance.current,
                    position: position,
                    title: worker.name,
                    content: buildContent(),
                    zIndex: selectedWorkerId === worker.id ? 100 : 1
                });

                marker.addListener('click', () => {
                    handleWorkerSelection(worker);
                    mapInstance.current.panTo(position);
                });

                currentMarkers[worker.id] = marker;
            }
        });

        // Cleanup removed markers
        Object.keys(currentMarkers).forEach((id) => {
            if (!activeWorkerIds.has(id)) {
                currentMarkers[id].map = null;
                delete currentMarkers[id];
            }
        });

    }, [filteredWorkers, selectedWorkerId]);

    const handleWorkerSelection = (worker: WorkerProfile) => {
        setSelectedWorkerId(prev => prev === worker.id ? null : worker.id);
    };

    const selectedWorker = filteredWorkers.find(w => w.id === selectedWorkerId);
    const activeJobForSelected = selectedWorker ? activeJobs.find(job => job.workerId === selectedWorker.id && ['ACCEPTED', 'IN_PROGRESS'].includes(job.status)) : null;
    const displayCategories = ['All', ...categories.filter(c => c.isActive).map(c => c.name)];

    // Filter ads by position
    const topAds = ads.filter(ad => ad.position === 'TOP' && ad.isActive);
    const bottomAds = ads.filter(ad => ad.position === 'BOTTOM' && ad.isActive);

    return (
        <div className="h-full w-full flex flex-col lg:flex-row bg-white dark:bg-slate-900 relative overflow-hidden">

            {/* Sidebar List for Desktop */}
            <div className={`
        ${isDesktop ? 'w-96 border-r flex' : 'hidden'} 
        bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 h-full flex-col shrink-0 z-40 animate-fadeIn
      `}>
                {/* TOP Ad Banner */}
                {topAds.length > 0 && <AdBanner ad={topAds[0]} />}

                <div className="p-6 border-b border-slate-100 dark:border-slate-800 shrink-0">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">{t.nearby_pros}</h2>
                        <div className="flex items-center gap-2">
                            <NotificationBell userRole={UserRole.CUSTOMER} />
                            <Button size="sm" onClick={onOpenPostJob} className="rounded-xl">
                                <Plus className="w-4 h-4 mr-1" /> Post Job
                            </Button>
                        </div>
                    </div>
                    <div className="relative" ref={searchRef}>
                        <div className="relative">
                            <input
                                type="text"
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    setIsSearchFocused(true);
                                }}
                                onFocus={() => setIsSearchFocused(true)}
                                placeholder={selectedCategory === 'All' ? t.search_placeholder : `Searching in ${selectedCategory}...`}
                                className="w-full pl-10 pr-10 py-3 bg-slate-50 dark:bg-slate-800 rounded-xl text-slate-900 dark:text-white text-sm outline-none font-medium border border-slate-200 dark:border-slate-700 focus:border-blue-500 transition-all placeholder:text-slate-400"
                            />
                            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                            {selectedCategory !== 'All' && (
                                <button
                                    onClick={() => setSelectedCategory('All')}
                                    className="absolute right-3 top-3.5 text-slate-400 hover:text-red-500"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Search Dropdown */}
                        {isSearchFocused && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-700 z-[100] max-h-64 overflow-y-auto overflow-x-hidden animate-slideDown">
                                <div className="p-2 space-y-1">
                                    {selectedCategory !== 'All' && (
                                        <button
                                            onClick={() => {
                                                setSelectedCategory('All');
                                                setIsSearchFocused(false);
                                                setQuery('');
                                            }}
                                            className="w-full text-left px-4 py-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-3 transition-colors border border-transparent hover:border-slate-100 dark:hover:border-slate-600"
                                        >
                                            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-400">
                                                <X className="w-4 h-4" />
                                            </div>
                                            <span className="text-sm font-bold text-slate-600 dark:text-slate-300">Clear Category Filter</span>
                                        </button>
                                    )}

                                    {categoriesToDisplay.length === 0 ? (
                                        <div className="p-8 text-center text-slate-400">
                                            <Search className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                            <p className="text-[10px] font-black uppercase tracking-widest">No services found</p>
                                        </div>
                                    ) : (
                                        categoriesToDisplay.map(cat => (
                                            <button
                                                key={cat.id}
                                                onClick={() => {
                                                    setSelectedCategory(cat.name);
                                                    setIsSearchFocused(false);
                                                    setQuery('');
                                                }}
                                                className="w-full text-left px-4 py-3 rounded-xl hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center justify-between group transition-all"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${cat.parentId ? 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-500 dark:text-indigo-400' : 'bg-blue-50 dark:bg-blue-900/40 text-blue-500 dark:text-blue-400'}`}>
                                                        {cat.parentId ? <Tag className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-black text-slate-800 dark:text-slate-200">{cat.name}</p>
                                                        {cat.parentId && (
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                                                Sub-service of {categories.find(p => p.id === cat.parentId)?.name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <IndianRupee className="w-3 h-3 text-blue-500" />
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide bg-slate-50/30 dark:bg-slate-900/50">
                    {filteredWorkers.map(worker => (
                        <WorkerCard
                            key={worker.id}
                            worker={worker}
                            isSelected={selectedWorkerId === worker.id}
                            onClick={() => onSelectWorker(worker)}
                            onBook={() => onBookWorker(worker)}
                        />
                    ))}
                </div>

                {/* BOTTOM Ad Banner */}
                {bottomAds.length > 0 && <AdBanner ad={bottomAds[0]} />}
            </div>

            {/* Map Content */}
            <div className="flex-1 relative bg-slate-50 overflow-hidden min-h-0">
                {!isDesktop && (
                    <div className="absolute top-4 left-4 right-4 z-[200]">
                        <div className="bg-white shadow-xl rounded-2xl border border-slate-100 relative overflow-visible" ref={searchRef}>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        setIsSearchFocused(true);
                                    }}
                                    onFocus={() => setIsSearchFocused(true)}
                                    placeholder={selectedCategory === 'All' ? t.search_placeholder : `Service: ${selectedCategory}`}
                                    className="w-full pl-10 pr-10 py-3 bg-slate-50 rounded-2xl text-slate-900 text-sm outline-none font-medium"
                                />
                                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
                                {selectedCategory !== 'All' && (
                                    <button
                                        onClick={() => setSelectedCategory('All')}
                                        className="absolute right-3 top-3.5 text-slate-400"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {/* Mobile Dropdown */}
                            {isSearchFocused && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-slate-100 max-h-64 overflow-y-auto animate-fadeIn">
                                    <div className="p-2 space-y-1">
                                        {categoriesToDisplay.map(cat => (
                                            <button
                                                key={cat.id}
                                                onClick={() => {
                                                    setSelectedCategory(cat.name);
                                                    setIsSearchFocused(false);
                                                    setQuery('');
                                                }}
                                                className="w-full text-left px-4 py-3 rounded-xl hover:bg-slate-50 flex items-center justify-between"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${cat.parentId ? 'bg-indigo-50 text-indigo-500' : 'bg-blue-50 text-blue-500'}`}>
                                                        {cat.parentId ? <Tag className="w-4 h-4" /> : <Layers className="w-4 h-4" />}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-black text-slate-800">{cat.name}</p>
                                                        {cat.parentId && (
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                                                                {categories.find(p => p.id === cat.parentId)?.name}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Real Google Map Container */}
                <div ref={mapRef} className="h-full w-full bg-slate-100"></div>

                {/* Mobile FAB */}
                {!isDesktop && !selectedWorker && (
                    <div className="absolute bottom-24 right-6 z-[80]">
                        <button
                            onClick={onOpenPostJob}
                            className="bg-slate-900 text-white w-14 h-14 rounded-2xl shadow-2xl flex items-center justify-center active:scale-90 transition-all border-2 border-white/20"
                        >
                            <Plus className="w-7 h-7" />
                        </button>
                    </div>
                )}
            </div>

            {/* Mobile-only Bottom Sheet */}
            {!isDesktop && (
                <div className={`
            absolute bottom-0 left-0 right-0 z-[100] transition-all duration-500 bg-white dark:bg-slate-900 shadow-[0_-20px_50px_rgba(0,0,0,0.1)] rounded-t-[2.5rem] flex flex-col overflow-hidden
            ${isSheetExpanded ? 'h-[85%]' : 'h-[30%]'}
        `}>
                    {/* TOP Ad Banner for Mobile */}
                    {topAds.length > 0 && <AdBanner ad={topAds[0]} />}

                    <div className="w-full flex flex-col items-center pt-3 pb-2 shrink-0 bg-white dark:bg-slate-900" onClick={() => setIsSheetExpanded(!isSheetExpanded)}>
                        <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full mb-3"></div>
                        <div className="flex items-center justify-between w-full px-6">
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t.nearby_pros} ({filteredWorkers.length})</span>
                            <div className="flex items-center gap-3">
                                <NotificationBell userRole={UserRole.CUSTOMER} />
                                <button
                                    onClick={(e) => { e.stopPropagation(); onOpenPostJob(); }}
                                    className="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest active:bg-blue-100"
                                >
                                    + Post Job
                                </button>
                                {isSheetExpanded ? <ChevronDown className="w-5 h-5 text-slate-300" /> : <ChevronUp className="w-5 h-5 text-slate-300" />}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3 scrollbar-hide bg-slate-50 dark:bg-slate-950">
                        {filteredWorkers.map(worker => (
                            <WorkerCard
                                key={worker.id}
                                worker={worker}
                                isSelected={selectedWorkerId === worker.id}
                                onClick={() => onSelectWorker(worker)}
                                onBook={() => onBookWorker(worker)}
                            />
                        ))}
                    </div>

                    {/* BOTTOM Ad Banner for Mobile */}
                    {bottomAds.length > 0 && <AdBanner ad={bottomAds[0]} />}
                </div>
            )}

            {/* Action Overlay for Selected Worker (Responsive) */}
            {selectedWorker && (
                <div className="absolute bottom-6 left-4 right-4 lg:left-auto lg:right-8 lg:bottom-8 z-[200] bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-[0_25px_60px_rgba(0,0,0,0.15)] p-6 animate-slideUp rounded-[2rem] w-auto lg:w-96">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-4">
                            <img src={selectedWorker.avatarUrl} className="w-14 h-14 rounded-2xl object-cover shadow-sm" alt="" />
                            <div className="min-w-0">
                                <h3 className="font-black text-slate-900 dark:text-white truncate leading-none">{selectedWorker.name}</h3>
                                <p className="text-[10px] text-blue-600 dark:text-blue-400 font-black uppercase tracking-widest mt-2">₹{selectedWorker.hourlyRate} / project</p>
                            </div>
                        </div>
                        <Button variant="ghost" size="sm" className="text-slate-400 font-black" onClick={() => onSelectWorker(selectedWorker)}>{t.identity}</Button>
                    </div>
                    <div className="flex gap-2">
                        {selectedWorker.isOnline ? (
                            <Button
                                fullWidth
                                className="h-14 rounded-2xl font-black uppercase tracking-widest text-[10px]"
                                onClick={() => { activeJobForSelected && onTrackWorker ? onTrackWorker(activeJobForSelected) : onBookWorker(selectedWorker); }}
                            >
                                {activeJobForSelected ? 'Track Active Job' : t.book_now}
                            </Button>
                        ) : (
                            <Button
                                fullWidth
                                disabled
                                className="h-14 rounded-2xl font-black uppercase tracking-widest text-[10px] bg-slate-100 text-slate-400 cursor-not-allowed"
                            >
                                <Clock className="w-4 h-4 mr-2" /> Worker Offline
                            </Button>
                        )}
                        <button onClick={() => setSelectedWorkerId(null)} className="w-14 h-14 flex items-center justify-center bg-slate-50 rounded-2xl text-slate-400 hover:text-slate-600">
                            <ChevronDown className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
